import { AppToBuildStr } from "../../appsConfig";
import { getDeviceId } from "@/utils/identity";
import { Clerk } from "@clerk/clerk-js";
import i18n, { getCurrentLanguage } from "@/i18n";
import { shouldResolveClientError } from "./resolveClientError";
import { RequestTimeoutError, raceAbort } from "./requestTimeout";

// Re-exported so callers can keep importing the fetch contract from one place.
export { RequestTimeoutError, isRequestTimeoutError } from "./requestTimeout";

let clerkInstance: Clerk | null = null;
let clerkPromise: Promise<Clerk> | null = null;

export function getClerkClient(): Promise<Clerk> {
  if (!clerkPromise) {
    const pk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY!;
    const clerk = new Clerk(pk);
    clerkPromise = clerk.load().then(() => {
      clerkInstance = clerk;

      // Listen for session changes (including automatic logouts)
      clerk.addListener((event) => {
        // If user is signed out (session becomes null), clear cache
        if (!event.session && event.user === null) {
          clearClerkSession();
        }
      });

      return clerk;
    });
  }
  return clerkPromise;
}

/** Call this after sign-in to refresh the session */
export async function refreshClerkSession(): Promise<void> {
  if (clerkInstance) {
    // Force reload the client to get fresh session data
    await clerkInstance.load({
      isSatellite: false,
      standardBrowser: true,
    });
  }
}

/** Call this on sign-out */
export function clearClerkSession() {
  clerkInstance = null;
  clerkPromise = null;
  freshToken = null;
  freshTokenInFlight = null;
}

// Every authed call mints its own token with `skipCache: true` so a stale JWT
// never survives a sign-in. Unbatched, that turns one app boot into ~15
// simultaneous POSTs to Clerk's token endpoint, and Clerk answers the burst
// with 429s — the failed mints surface as random "no auth token" errors and a
// half-loaded app. Share one in-flight mint and reuse its result for a short
// window: still a freshly-minted token (Clerk's own tokens live ~60s), one
// request instead of fifteen.
const FRESH_TOKEN_TTL_MS = 10_000;
let freshToken: { value: string; expiresAt: number } | null = null;
let freshTokenInFlight: Promise<string> | null = null;

export async function getFreshAuthToken(): Promise<string> {
  const clerk = await getClerkClient();

  // Check if session exists before trying to get token
  if (!clerk.session) {
    throw new Error(i18n.t("errors.no_active_session"));
  }

  if (freshToken && freshToken.expiresAt > Date.now()) return freshToken.value;

  if (!freshTokenInFlight) {
    const session = clerk.session;
    freshTokenInFlight = (async () => {
      try {
        const token = await session.getToken({
          template: undefined,
          leewayInSeconds: undefined,
          skipCache: true,
        });
        if (!token) throw new Error(i18n.t("errors.no_auth_token"));
        freshToken = { value: token, expiresAt: Date.now() + FRESH_TOKEN_TTL_MS };
        return token;
      } finally {
        freshTokenInFlight = null;
      }
    })();
  }

  return freshTokenInFlight;
}

// Vite envs
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

/** Create an authed fetch function (same shape as your RN version) */
export function useAuthedFetch(appToBuild: AppToBuildStr) {
  return async function authedFetch<T>(
    path: string,
    init?: RequestInit & {
      json?: any;
      resolveClientErrors?: boolean;
      /**
       * Abort the request after this many ms and throw a `RequestTimeoutError`.
       *
       * OPT-IN on purpose. A blanket timeout here would be wrong: `getAllQuestions`
       * pulls ~2.7 MB and can legitimately run for a long time on the weak mobile
       * connections this is meant to protect — capping it would break the app for
       * exactly those users. Set it on short, interactive requests whose UI would
       * otherwise hang with no way out.
       */
      timeoutMs?: number;
    },
    noAuth?: boolean
  ): Promise<T> {
    // Deadline starts HERE, not just before fetch(). `getFreshAuthToken` makes its own
    // network call to Clerk and has no timeout, so arming the timer later meant a stalled
    // token mint was completely unprotected — the request never started, so the guard
    // never armed, and the student saw exactly the hang this exists to prevent.
    const controller = init?.timeoutMs ? new AbortController() : null;
    let timedOut = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (controller && init?.timeoutMs) {
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, init.timeoutMs);

      // Compose the caller's own signal with ours, so neither is lost.
      if (init.signal) {
        if (init.signal.aborted) controller.abort();
        else
          init.signal.addEventListener("abort", () => controller.abort(), {
            once: true,
          });
      }
    }

    // Neither the Clerk token mint nor the device-id lookup accepts an AbortSignal, so
    // the deadline has to be applied by racing them — checking `timedOut` after the
    // await is useless when the await itself is what never returns.
    const withDeadline = <V,>(work: Promise<V>): Promise<V> =>
      controller
        ? raceAbort(work, controller.signal, () =>
            // Our controller aborts for TWO reasons: the deadline elapsed, or the
            // caller's own signal fired. Only the first is a timeout — reporting a
            // deliberate cancellation as one would show the student "this took too
            // long" for something they cancelled themselves.
            timedOut
              ? new RequestTimeoutError(path, init!.timeoutMs!)
              : Object.assign(new Error("The operation was aborted."), {
                  name: "AbortError",
                })
          )
        : work;

    try {
      let jwt = "";
      if (!noAuth) {
        jwt = await withDeadline(getFreshAuthToken());
      }

      const deviceId = await withDeadline(getDeviceId());

      const headers: Record<string, string> = {
        ...(init?.headers as Record<string, string>),
        "Content-Type": "application/json",
        "X-App-Type": appToBuild,
        "X-Device-Id": deviceId,
        "X-Language": getCurrentLanguage(),
        Authorization: `Bearer ${jwt}`,
      };

      const res = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers,
        body: init?.json ? JSON.stringify(init.json) : init?.body,
        signal: controller ? controller.signal : init?.signal,
      });

      if (!res.ok) {
        const text = await res.text();

        // If 401, clear the session cache
        if (res.status === 401) {
          clearClerkSession();
        }

        // Some endpoints (e.g. referral redeem) return typed 4xx JSON bodies that
        // are final outcomes — resolve those so callers branch on the body and
        // only retry on thrown errors. shouldResolveClientError() keeps 401
        // (re-auth) and 429 (transient `rate_limited`) on the throw/retry path. A
        // typed app response always carries an `ok` discriminant; an `ok`-less
        // body (gateway/proxy 4xx) also falls through to throw.
        if (init?.resolveClientErrors && shouldResolveClientError(res.status)) {
          try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === "object" && "ok" in parsed) {
              return parsed as T;
            }
          } catch {
            // not a typed body — fall through to throw
          }
        }

        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      // AWAITED, not returned as a promise: `fetch` resolves when the response HEADERS
      // arrive, so returning `res.json()` unawaited would leave the BODY read outside
      // the timeout — a stalled body would hang forever with the timer already cleared,
      // reproducing the original bug one step later.
      return (await res.json()) as T;
    } catch (e) {
      // An AbortError raised by OUR controller is a timeout. One raised by the caller's
      // own signal is a deliberate cancel and is rethrown untouched.
      if (timedOut) {
        throw new RequestTimeoutError(path, init!.timeoutMs!);
      }
      throw e;
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  };
}

export const getUser = async () => {
  const clerk = await getClerkClient();

  // Check if session exists before trying to get token
  if (!clerk.session) {
    throw new Error("No active session. Please sign in.");
  }

  return clerk.session.user;
};
